import { useEffect, useState } from "react";
import { api, ApiError } from "../lib/api.js";

interface Session {
  id: string;
  device_id: string;
  hostname: string | null;
  label: string | null;
  mode: string;
  status: string;
  started_at: string;
  ended_at: string | null;
  end_reason: string | null;
}

interface AuditEntry {
  id: string;
  action: string;
  device_id: string | null;
  hostname: string | null;
  created_at: string;
}

function getDurationMinutes(start: string, end: string | null): number {
  const startTime = new Date(start).getTime();
  const endTime = end ? new Date(end).getTime() : Date.now();
  return Math.floor((endTime - startTime) / (1000 * 60));
}

function getStatusColor(status: string) {
  if (status === "completed" || status === "ended") return "text-green-400 bg-green-400/10";
  if (status === "active") return "text-blue-400 bg-blue-400/10";
  if (status === "failed" || status === "rejected" || status === "timeout")
    return "text-red-400 bg-red-400/10";
  return "text-yellow-400 bg-yellow-400/10";
}

function getModeIcon(mode: string) {
  if (mode === "view") return "👁️";
  if (mode === "control") return "🖱️";
  return "🔗";
}

interface RecordingSummary {
  session_id: string;
  n: number;
}

export function SessionsPage() {
  const [sessions, setSessions] = useState<Session[] | null>(null);
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  // v2 recordings: count per session (summary) + which session is currently opening.
  const [recCounts, setRecCounts] = useState<Record<string, number>>({});
  const [openingRec, setOpeningRec] = useState<string | null>(null);

  useEffect(() => {
    api<Session[]>("/v1/sessions")
      .then(setSessions)
      .catch((err) => setError(err instanceof ApiError ? err.code : String(err)));
    api<AuditEntry[]>("/v1/sessions/audit")
      .then(setAudit)
      .catch(() => {
        /* audit is non-critical */
      });
    // v2 recordings summary (empty [] on an old gateway / before the migration).
    api<RecordingSummary[]>("/v1/sessions/recordings")
      .then((rows) => {
        const m: Record<string, number> = {};
        for (const r of rows) m[r.session_id] = r.n;
        setRecCounts(m);
      })
      .catch(() => {
        /* recordings are non-critical / may be disabled */
      });
  }, []);

  // Lazily fetch the signed playback URL(s) for a session and open the newest.
  async function playRecording(sessionId: string) {
    setOpeningRec(sessionId);
    try {
      const rows = await api<Array<{ id: string; created_at: string; playbackUrl?: string }>>(
        `/v1/sessions/${sessionId}/recordings`
      );
      const newest = rows
        .filter((r) => r.playbackUrl)
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
      if (newest?.playbackUrl) window.open(newest.playbackUrl, "_blank", "noopener");
    } catch {
      /* signed URL unavailable */
    } finally {
      setOpeningRec(null);
    }
  }

  const stats = sessions ? {
    total: sessions.length,
    active: sessions.filter(s => !s.ended_at).length,
    totalMinutes: sessions.reduce((acc, s) => acc + getDurationMinutes(s.started_at, s.ended_at), 0)
  } : null;

  return (
    <section className="space-y-8 pb-8">
      <div>
        <h1 className="text-4xl font-bold text-white mb-2">Session History</h1>
        <p className="text-white/50">Monitor and analyze your remote access sessions</p>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-300 text-sm">
          {error}
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="glass p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/50 text-sm mb-1">Total Sessions</p>
                <p className="text-3xl font-bold text-white">{stats.total}</p>
              </div>
              <svg className="w-8 h-8 text-blue-400/30" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z" />
              </svg>
            </div>
          </div>

          <div className="glass p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/50 text-sm mb-1">Active Now</p>
                <p className="text-3xl font-bold text-green-400">{stats.active}</p>
              </div>
              <svg className="w-8 h-8 text-green-400/30 animate-pulse" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
              </svg>
            </div>
          </div>

          <div className="glass p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/50 text-sm mb-1">Total Duration</p>
                <p className="text-3xl font-bold text-white">{stats.totalMinutes}</p>
                <p className="text-xs text-white/40 mt-1">minutes</p>
              </div>
              <svg className="w-8 h-8 text-white/20" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z" />
              </svg>
            </div>
          </div>
        </div>
      )}

      {sessions === null ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <svg className="w-12 h-12 mx-auto mb-3 text-white/30 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v16m8-8H4" />
            </svg>
            <p className="text-white/50">Loading sessions…</p>
          </div>
        </div>
      ) : sessions.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <svg className="w-16 h-16 mx-auto mb-4 text-white/20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="text-xl font-semibold text-white mb-2">No sessions yet</h3>
            <p className="text-white/50">Connect to a device to start a session</p>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {sessions.map((s) => {
            const duration = getDurationMinutes(s.started_at, s.ended_at);
            const startDate = new Date(s.started_at);
            const isActive = !s.ended_at;
            const recN = recCounts[s.id] ?? 0;
            return (
              <div
                key={s.id}
                className="glass p-4 border-l-4 border-l-blue-500 hover:border-l-blue-400 transition"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-2xl">{getModeIcon(s.mode)}</span>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-white">
                            {s.mode === "view" ? "View-Only Session" : "Control Session"}
                          </h3>
                          <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(isActive ? "active" : s.status)}`}>
                            <div className={`w-1.5 h-1.5 rounded-full ${isActive ? "bg-blue-400 animate-pulse" : "bg-current"}`} />
                            {isActive ? "Active" : s.status}
                          </span>
                        </div>
                        <p className="text-xs text-white/50 mt-1">
                          {startDate.toLocaleDateString()} at {startDate.toLocaleTimeString()}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                      <div>
                        <p className="text-white/50">Device</p>
                        <p className="text-white/70 mt-0.5 truncate">
                          {s.label || s.hostname || `${s.device_id.slice(0, 12)}…`}
                        </p>
                      </div>
                      <div>
                        <p className="text-white/50">Duration</p>
                        <p className="text-white/70 font-medium mt-0.5">{duration} min</p>
                      </div>
                      <div>
                        <p className="text-white/50">Mode</p>
                        <p className="text-white/70 capitalize mt-0.5">{s.mode}</p>
                      </div>
                      {s.ended_at && (
                        <div>
                          <p className="text-white/50">Ended</p>
                          <p className="text-white/70 mt-0.5">{new Date(s.ended_at).toLocaleTimeString()}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {recN > 0 && (
                      <button
                        type="button"
                        onClick={() => playRecording(s.id)}
                        disabled={openingRec === s.id}
                        title={`Play recording${recN > 1 ? ` (${recN})` : ""}`}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-red-500/15 hover:bg-red-500/25 text-red-200 text-xs font-medium transition disabled:opacity-50"
                      >
                        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                        {openingRec === s.id ? "Opening…" : recN > 1 ? `${recN} recordings` : "Recording"}
                      </button>
                    )}
                    <button
                      type="button"
                      className="p-2 hover:bg-white/10 rounded-lg transition text-white/70 hover:text-white"
                      title="View details"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 5a1 1 0 011-1h4a1 1 0 011 1v2H9V5zm0 4h6v8H9V9z" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {audit.length > 0 && (
        <div className="glass p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Audit log</h2>
          <div className="space-y-1.5 max-h-96 overflow-y-auto">
            {audit.map((a) => (
              <div
                key={a.id}
                className="flex items-center gap-3 text-xs py-1.5 border-b border-white/5 last:border-0"
              >
                <span className="font-mono text-blue-300 w-28 flex-shrink-0">{a.action}</span>
                <span className="text-white/60 flex-1 truncate">
                  {a.hostname ?? (a.device_id ? `${a.device_id.slice(0, 8)}…` : "—")}
                </span>
                <span className="text-white/40 flex-shrink-0">
                  {new Date(a.created_at).toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
